import React, { Fragment, useEffect, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Navbar from "../HomeScreen/Navbar";
import Footer from "../HomeScreen/bottomsection/Footer";
import Loader from "../../layout/loader";
import "./cartpage.css";
import { textRapForNameSearch } from "../../utils/textRapUtils";
import {
  cartAction,
  cartRemoveAction,
  clearErrorAction,
  allcartItemAction,
} from "../../redux/actions/cartAction";
import { addProductToWishListAction } from "../../redux/actions/wishListAction";
import { REMOVE_FROM_CART_RESET } from "../../redux/constants/cartConstants";
import swal from "sweetalert";
import { Helmet } from "react-helmet";

// Icons
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveShoppingCartIcon from "@material-ui/icons/RemoveShoppingCart";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";

function Cartpage() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { isAuthenticated } = useSelector((state) => state.user);
  const { carts, loading } = useSelector((state) => state.carts);
  const {
    Removed,
    loading: cartRemoveLoading,
    error: itemRemoveFromCartError,
  } = useSelector((state) => state.removeCart);

  // Calculate total units & gross price
  const cartTotal = useMemo(() => {
    return (carts || []).reduce(
      (acc, item) => acc + (Number(item.quantity) || 1) * (Number(item.price) || 0),
      0
    );
  }, [carts]);

  const totalItemsCount = useMemo(() => {
    return (carts || []).reduce(
      (acc, item) => acc + (Number(item.quantity) || 1),
      0
    );
  }, [carts]);

  // Free delivery threshold: ₹499
  const freeDeliveryThreshold = 499;
  const isFreeDelivery = cartTotal >= freeDeliveryThreshold;
  const amountToFreeDelivery = Math.max(0, freeDeliveryThreshold - cartTotal);
  const deliveryProgress = Math.min(
    100,
    Math.round((cartTotal / freeDeliveryThreshold) * 100)
  );

  const removeCartItems = (item) => {
    const idToRemove = item.product || item._id;
    dispatch(cartRemoveAction(idToRemove));
  };

  const updateQuantity = (item, delta) => {
    const currentQty = Number(item.quantity) || 1;
    const newQty = currentQty + delta;

    if (newQty < 1) {
      swal({
        title: "Remove Item?",
        text: `Do you want to remove ${item.name || "this product"} from your shopping bag?`,
        icon: "warning",
        buttons: ["Keep Item", "Remove"],
        dangerMode: true,
      }).then((willRemove) => {
        if (willRemove) {
          removeCartItems(item);
        }
      });
      return;
    }

    dispatch(
      cartAction(
        item.product,
        newQty,
        item.size || 0,
        item.colour || "Default",
        item.selectBox || "0",
        item.productShippingCharges || 0
      )
    );
  };

  const saveForLaterHandler = (item) => {
    if (item.product) {
      dispatch(addProductToWishListAction(item.product));
    }
    removeCartItems(item);
    swal({
      title: "Saved for Later",
      text: `${item.name || "Item"} has been moved to your wishlist!`,
      icon: "success",
      timer: 1800,
      buttons: false,
    });
  };

  useEffect(() => {
    if (itemRemoveFromCartError) {
      swal({
        title: "Remove Error",
        text: itemRemoveFromCartError,
        icon: "error",
        button: "OK",
      });
      dispatch(clearErrorAction());
    }

    if (cartRemoveLoading) {
      swal("Updating Shopping Bag...", { button: false });
    }

    if (Removed) {
      swal({ title: "Removed", text: Removed, icon: "success", timer: 1500, buttons: false });
      dispatch({ type: REMOVE_FROM_CART_RESET });
    }

    dispatch(allcartItemAction());
  }, [dispatch, Removed, cartRemoveLoading, itemRemoveFromCartError]);

  const checkoutHandler = () => {
    if (!isAuthenticated) {
      swal({
        title: "Sign In Required",
        text: "Please sign in or create an account to proceed with checkout. Your cart will be saved!",
        icon: "info",
        buttons: ["Continue Shopping", "Sign In & Checkout"],
      }).then((willLogin) => {
        if (willLogin) {
          history.push("/Login?redirect=Checkout");
        }
      });
      return;
    }
    history.push("/Checkout");
  };

  return (
    <Fragment>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=2.0"
        />
        <meta
          name="description"
          content="Review your shopping bag, apply discounts, and checkout securely with IM Online Shopping."
        />
        <meta
          name="keywords"
          content="cart page, shopping bag, imonline.shop cart, checkout, local store delivery"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Shopping Bag | IM Online Shopping" />
        <meta
          property="og:description"
          content="Review your selected items and checkout with local express delivery."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="IMONLINE.SHOP" />
      </Helmet>

      {/* Global Navbar */}
      <Navbar />

      {loading ? (
        <Loader />
      ) : !carts || carts.length === 0 ? (
        <main className="im-cart-empty-page">
          <div className="im-cart-empty-card">
            <div className="im-cart-empty-icon-box">
              <RemoveShoppingCartIcon className="im-cart-empty-icon" />
            </div>
            <h1>Your Shopping Bag is Empty</h1>
            <p>
              Looks like you haven't added anything yet. Explore authentic products from verified local shops in your neighborhood!
            </p>

            <div className="im-cart-empty-actions">
              <Link to="/marketplace/search?type=shops" className="im-cart-btn primary">
                <StorefrontOutlinedIcon />
                <span>Explore Nearby Shops</span>
              </Link>
              <Link to="/Product" className="im-cart-btn secondary">
                <ShoppingBagOutlinedIcon />
                <span>Browse All Products</span>
              </Link>
            </div>

            {!isAuthenticated && (
              <div className="im-cart-empty-signin">
                <span>Already have an account?</span>
                <Link to="/Login?redirect=Cart">
                  <LockOutlinedIcon style={{ fontSize: "15px" }} /> Sign in to view saved cart
                </Link>
              </div>
            )}
          </div>
        </main>
      ) : (
        <main className="im-cart-page">
          <div className="im-cart-container">
            {/* Page Header / Hero Strip */}
            <header className="im-cart-header">
              <div className="im-cart-header-copy">
                <div className="im-cart-title-row">
                  <h1>Shopping Bag</h1>
                  <span className="im-cart-pill-count">
                    {totalItemsCount} {totalItemsCount === 1 ? "item" : "items"}
                  </span>
                </div>
                <p>Review items, adjust quantities, and checkout with fast local delivery.</p>
              </div>

              {/* Free Delivery Goal Strip */}
              <div className="im-cart-delivery-status">
                <div className="im-delivery-status-header">
                  <LocalShippingOutlinedIcon className="im-delivery-icon" />
                  {isFreeDelivery ? (
                    <span className="im-delivery-msg unlocked">
                      <strong>🎉 Free Express Delivery</strong> unlocked for this order!
                    </span>
                  ) : (
                    <span className="im-delivery-msg">
                      Add <strong>₹{amountToFreeDelivery}</strong> more for <strong>FREE Delivery</strong>
                    </span>
                  )}
                </div>
                <div className="im-delivery-progress-track">
                  <div
                    className={`im-delivery-progress-fill ${isFreeDelivery ? "complete" : ""}`}
                    style={{ width: `${deliveryProgress}%` }}
                  />
                </div>
              </div>
            </header>

            {/* Main Cart Content Grid */}
            <div className="im-cart-grid">
              {/* Left Column: Cart Items List */}
              <section className="im-cart-items-section" aria-label="Items in bag">
                <div className="im-cart-section-topbar">
                  <span>Product Details</span>
                  <span className="im-hide-mobile">Quantity</span>
                  <span className="im-hide-mobile">Subtotal</span>
                </div>

                <div className="im-cart-items-list">
                  {carts.map((item) => {
                    const itemQty = Number(item.quantity) || 1;
                    const itemUnitPrice = Number(item.price) || 0;
                    const itemSubtotal = itemUnitPrice * itemQty;

                    return (
                      <article className="im-cart-item-card" key={item._id || item.product}>
                        <div className="im-cart-item-main">
                          {/* Product Image */}
                          <Link
                            to={`/ProductDetail/${item.product}`}
                            className="im-cart-img-wrap"
                          >
                            <img
                              src={item.image || "/favicon.ico"}
                              alt={item.name || "Product"}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/favicon.ico";
                              }}
                            />
                          </Link>

                          {/* Product Details */}
                          <div className="im-cart-item-details">
                            <Link
                              to={`/ProductDetail/${item.product}`}
                              className="im-cart-item-name"
                            >
                              {textRapForNameSearch(item.name || "Product")}
                            </Link>

                            {/* Tags: Price, Size, Color */}
                            <div className="im-cart-tags-wrap">
                              <span className="im-cart-tag price">
                                ₹{itemUnitPrice}
                              </span>
                              {item.size ? (
                                <span className="im-cart-tag">
                                  Size: <strong>{item.size}</strong>
                                </span>
                              ) : null}
                              {item.colour && item.colour !== "Default" ? (
                                <span className="im-cart-tag">
                                  Color: <strong>{item.colour}</strong>
                                </span>
                              ) : null}
                            </div>

                            {/* Stock & Delivery Micro-badge */}
                            <div className="im-cart-shipping-badge">
                              <CheckCircleIcon />
                              <span>In Stock • Fast Local Delivery</span>
                            </div>

                            {/* Mobile Row: Stepper + Total Price */}
                            <div className="im-cart-mobile-actions-row">
                              <div className="im-cart-stepper">
                                <button
                                  type="button"
                                  className="im-stepper-btn"
                                  onClick={() => updateQuantity(item, -1)}
                                  aria-label="Decrease quantity"
                                >
                                  {itemQty === 1 ? (
                                    <DeleteOutlineIcon className="im-stepper-trash" />
                                  ) : (
                                    <RemoveIcon />
                                  )}
                                </button>
                                <span className="im-stepper-val">{itemQty}</span>
                                <button
                                  type="button"
                                  className="im-stepper-btn"
                                  onClick={() => updateQuantity(item, 1)}
                                  aria-label="Increase quantity"
                                >
                                  <AddIcon />
                                </button>
                              </div>

                              <div className="im-cart-mobile-price">
                                <strong>₹{itemSubtotal}</strong>
                                {itemQty > 1 && (
                                  <span className="im-unit-label">(₹{itemUnitPrice} each)</span>
                                )}
                              </div>
                            </div>

                            {/* Secondary Action Links: Wishlist & Delete */}
                            <div className="im-cart-action-links">
                              <button
                                type="button"
                                className="im-action-btn-link wishlist"
                                onClick={() => saveForLaterHandler(item)}
                              >
                                <FavoriteBorderIcon />
                                <span>Save for Later</span>
                              </button>
                              <button
                                type="button"
                                className="im-action-btn-link delete"
                                onClick={() => removeCartItems(item)}
                              >
                                <DeleteOutlineIcon />
                                <span>Remove</span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Desktop Quantity Stepper */}
                        <div className="im-cart-item-stepper-col im-hide-mobile">
                          <div className="im-cart-stepper">
                            <button
                              type="button"
                              className="im-stepper-btn"
                              onClick={() => updateQuantity(item, -1)}
                              aria-label="Decrease quantity"
                            >
                              {itemQty === 1 ? (
                                <DeleteOutlineIcon className="im-stepper-trash" />
                              ) : (
                                <RemoveIcon />
                              )}
                            </button>
                            <span className="im-stepper-val">{itemQty}</span>
                            <button
                              type="button"
                              className="im-stepper-btn"
                              onClick={() => updateQuantity(item, 1)}
                              aria-label="Increase quantity"
                            >
                              <AddIcon />
                            </button>
                          </div>
                        </div>

                        {/* Desktop Subtotal & Remove Col */}
                        <div className="im-cart-item-price-col im-hide-mobile">
                          <strong>₹{itemSubtotal}</strong>
                          <button
                            type="button"
                            className="im-desktop-remove-btn"
                            title="Remove product"
                            onClick={() => removeCartItems(item)}
                          >
                            <DeleteOutlineIcon />
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>

                {/* Back to Catalogue */}
                <div className="im-cart-continue-strip">
                  <Link to="/Product" className="im-continue-link">
                    ← Continue Shopping
                  </Link>
                </div>
              </section>

              {/* Right Column: Order Summary Card */}
              <aside className="im-cart-summary-section">
                <div className="im-cart-summary-card">
                  <h2>Order Summary</h2>

                  <div className="im-summary-breakdown">
                    <div className="im-summary-row">
                      <span>Total Units</span>
                      <b>{totalItemsCount} items</b>
                    </div>
                    <div className="im-summary-row">
                      <span>Items Subtotal</span>
                      <b>₹{cartTotal}</b>
                    </div>
                    <div className="im-summary-row">
                      <span>Delivery Fee</span>
                      {isFreeDelivery ? (
                        <b className="im-free-tag">FREE</b>
                      ) : (
                        <span className="im-delivery-fee-note">Calculated at checkout</span>
                      )}
                    </div>
                    <div className="im-summary-row muted">
                      <span>Taxes & Duties</span>
                      <span>Included</span>
                    </div>

                    <div className="im-summary-divider" />

                    <div className="im-summary-total-row">
                      <div>
                        <strong>Estimated Total</strong>
                        <span className="im-subtext">Inclusive of all local taxes</span>
                      </div>
                      <strong className="im-total-price">₹{cartTotal}</strong>
                    </div>
                  </div>

                  {/* Primary Checkout CTA */}
                  <button
                    type="button"
                    className="im-checkout-btn"
                    onClick={checkoutHandler}
                  >
                    <span>
                      {!isAuthenticated ? "Sign In to Checkout" : "Proceed to Checkout"}
                    </span>
                    <ArrowForwardIcon />
                  </button>

                  {/* Trust Reassurance Badges */}
                  <div className="im-summary-trust-grid">
                    <div className="im-trust-item">
                      <SecurityOutlinedIcon />
                      <div>
                        <strong>256-Bit SSL Encrypted</strong>
                        <span>Safe and secure payments</span>
                      </div>
                    </div>
                    <div className="im-trust-item">
                      <VerifiedUserOutlinedIcon />
                      <div>
                        <strong>Authentic Local Stores</strong>
                        <span>100% verified shop partners</span>
                      </div>
                    </div>
                    <div className="im-trust-item">
                      <LocalShippingOutlinedIcon />
                      <div>
                        <strong>Fast Doorstep Pickup</strong>
                        <span>Quick delivery & easy returns</span>
                      </div>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>

          {/* Sticky Mobile Checkout Dock */}
          <div className="im-mobile-checkout-dock">
            <div className="im-dock-inner">
              <div className="im-dock-price-col">
                <span className="im-dock-label">Total ({totalItemsCount} {totalItemsCount === 1 ? 'item' : 'items'}):</span>
                <strong className="im-dock-price">₹{cartTotal}</strong>
              </div>
              <button
                type="button"
                className="im-dock-checkout-btn"
                onClick={checkoutHandler}
              >
                <span>{!isAuthenticated ? "Sign In" : "Checkout"}</span>
                <ArrowForwardIcon />
              </button>
            </div>
          </div>
        </main>
      )}

      {/* Footer */}
      <Footer />
    </Fragment>
  );
}

export default Cartpage;
